import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Form,
} from 'reactstrap';
import Select from 'react-select';

import images from 'assets/images';
import AppButton from 'components/App/Button';
import {
  CUSTOMER_ACCOUNT_TYPE_OPTIONS,
  CUSTOMER_LABEL_TO_ACCOUNT_TYPE,
} from 'constants/customerAccountType';

import styles from './ModalUpgradeCustomerAccount.module.scss';

function ModalUpgradeCustomerAccount({ isOpen, onToggleModal, user, onSubmit }) {
  const [selectedType, setSelectedType] = useState(null);

  const currentUserLevel = useMemo(() => {
    if (!user?.accountType) return '';
    return CUSTOMER_LABEL_TO_ACCOUNT_TYPE[user.accountType] || user.accountType;
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      const currentOption = CUSTOMER_ACCOUNT_TYPE_OPTIONS.find(
        (opt) => opt.value === currentUserLevel,
      );
      setSelectedType(currentOption || null);
    } else {
      setSelectedType(null);
    }
  }, [isOpen, user, currentUserLevel]);

  const handleCancel = useCallback(() => {
    onToggleModal();
  }, [onToggleModal]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!selectedType || !user) return;
    onSubmit?.(user._id, selectedType.value);
    onToggleModal();
  }, [selectedType, user, onSubmit, onToggleModal]);

  const getAvatarInitials = useCallback((name) => {
    if (!name) return 'TP';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      const first = parts[0].charAt(0).toUpperCase();
      const last = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${first}${last}`;
    }
    return name.slice(0, 2).toUpperCase();
  }, []);

  const CloseButton = useMemo(() => (
    <button className={styles.ButtonClose} onClick={handleCancel} type="button" aria-label="Close">
      <img src={images.modalClose.default} alt="" className={styles.ImgClose} />
    </button>
  ), [handleCancel]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      className={styles.Modal}
      contentClassName={styles.ModalContent}
    >
      <ModalHeader
        toggle={handleCancel}
        className={styles.ModalHeader}
        close={CloseButton}
      >
        <span className={styles.Title}>Nâng cấp tài khoản</span>
      </ModalHeader>
      <ModalBody className={styles.ModalBody}>
        {user && (
          <div className={styles.UserInfoBox}>
            <div className={styles.AvatarWrapper}>
              {getAvatarInitials(user.fullName)}
            </div>
            <div className={styles.UserDetails}>
              <div className={styles.UserName}>{user.fullName}</div>
              <div className={styles.UserRole}>{user.accountType}</div>
            </div>
          </div>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <div className={styles.FormGroup}>
            <label className={styles.Label}>Bạn muốn nâng cấp lên loại tài khoản nào</label>
            <Select
              options={CUSTOMER_ACCOUNT_TYPE_OPTIONS}
              value={selectedType}
              onChange={setSelectedType}
              placeholder="Chọn loại tài khoản"
              isSearchable={false}
              classNamePrefix="select2-selection"
              components={{ IndicatorSeparator: null }}
            />
          </div>

          <div className={styles.ButtonRow}>
            <AppButton
              type="button"
              classes={styles.BtnCancel}
              onClick={handleCancel}
              color="PRIMARY"
            >
              Hủy
            </AppButton>
            <AppButton
              type="submit"
              classes={styles.BtnSubmit}
              disabled={!selectedType || (user && selectedType.value === currentUserLevel)}
            >
              Xác nhận
            </AppButton>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}

ModalUpgradeCustomerAccount.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string,
    fullName: PropTypes.string,
    accountType: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    lastLogin: PropTypes.string,
    status: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
};

ModalUpgradeCustomerAccount.defaultProps = {
  user: null,
  onSubmit: undefined,
};

export default React.memo(ModalUpgradeCustomerAccount);
